import { HttpClient } from "@angular/common/http";
import { Injectable, inject, effect } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable, Subscription } from "rxjs";
import { ApiService } from "src/app/shared/services/api.service";
import { IViewStateCenter } from "src/app/shared/types/interfaces/map.interface";
import { basemaps, colorSchemes } from "src/app/shared/config/basemaps.config";
import { mapView } from "src/app/shared/config/mapview.config";
import { AuthService } from "src/app/core/services/auth.service";
import { CustomViewStatus } from "src/app/shared/types/enums/map.enums";
import { LayerVisibilityService } from "./layer-visibility.service";
import { CustomViewActions, MapActions } from "src/app/ngrx/actions";
import {
  buildAssetTypesForRendering,
  filterAssetTypesByLayerDisplayList,
} from "src/app/helpers/utils/asset-type.utils";
import {
  ICreateCustomViewResponse,
  IDeleteCustomViewResponse,
  IReadCustomViewResponse,
  IUpdateCustomViewResponse,
  ICustomView,
  ICustomViewInitialState,
  ICustomViewPayload,
  IDeleteCustomViewRequestPayload,
} from "src/app/shared/types/interfaces/custom-view.interface";
import { CircuitColorStyleScheme, IBasemapsConfig, IColorSchemesConfig } from "../types/interfaces/config.interfaces";
import { selectJurisdictionsettings } from "src/app/ngrx/selectors/map";
import { JurisdictionKey } from "src/app/shared/types/enums/map.enums";
import { systemPresetViewConfig } from "../config/custom-view.config";
import { MapViewService } from "./map-view.service";
import { ExtensionService } from "src/app/shared/services/extension.service";
import { environment } from "src/environments/environment";
import { getDefaultColorScheme } from "src/app/features/map/map/configs/map-view.config";
import { Extensions } from "src/app/shared/types/enums/extensions.enums";

@Injectable({
  providedIn: "root",
})
export class CustomViewService {
  private store = inject(Store);
  private authService = inject(AuthService);
  private mapViewService = inject(MapViewService);
  private layerVisibilityService = inject(LayerVisibilityService);
  private extensionService = inject(ExtensionService);
  private api = environment.api;
  private httpClient = inject(HttpClient);

  // observable
  subscriptions: Subscription[] = [];
  private jurisdictions$: Observable<JurisdictionKey[]> = this.store.select(selectJurisdictionsettings);

  // these properties store current ngrx state. they are initialized with DEFAULT state
  private basemapName: string;
  private colorScheme: CircuitColorStyleScheme;
  private viewStateCenter: IViewStateCenter = mapView.viewState.center;
  private viewStateZoom: number = mapView.viewState.zoom;
  private jurisdictionCodes: JurisdictionKey[] = [JurisdictionKey.ALL];
  private customViewName: string = "Default"; //initialState[0].customViewName;
  private isDefaultCustomView: boolean = false; //initialState.isDefaultCustomView;

  constructor() {
    this.basemapName = this.getDefaultMapStyleConfigName(basemaps);
    this.colorScheme = getDefaultColorScheme();

    // Track viewState changes from MapViewService using effect
    effect(() => {
      const viewState = this.mapViewService.viewState();
      if (viewState?.style) {
        // obtain name by looking up in basemaps config
        this.basemapName = basemaps[viewState.style].name;
      }
      if (viewState?.colorScheme) {
        // obtain name by looking up in colorScheme config
        this.colorScheme = viewState.colorScheme;
      }
      if (viewState?.center) {
        this.viewStateCenter = viewState.center;
      }
      if (viewState?.zoom) {
        this.viewStateZoom = viewState.zoom;
      }
    });

    this.subscriptions.push(
      this.jurisdictions$.subscribe((jurisdictions: JurisdictionKey[]) => {
        this.jurisdictionCodes = jurisdictions;
      })
    );
  }

  /**
   * use back-end user preferences CRUD API to read customView
   * @param userId
   * @returns customView matching userId
   */
  public readCustomView = (userId: string): Observable<IReadCustomViewResponse> => {
    const url = `${this.api}/user-preferences?action=R&user_id=${userId}`;
    const response: any = this.httpClient.get(url);
    return response;
  };

  /**
   * use back-end user preferences CRUD API to create a customView
   * @param customViewPayload
   * @returns true if created succesfully
   */
  public createCustomView = (customViewPayload: ICustomViewPayload): Observable<ICreateCustomViewResponse> => {
    const url = `${this.api}/user-preferences?action=C`;

    const body: string = JSON.stringify(customViewPayload);

    const response: any = this.httpClient.post(url, body);
    return response;
  };

  /**
   * use back-end user preferences CRUD API to update customView with new properties.
   * matching is based on customView.id. customView can specify all properties or
   * just some.
   * @param customViewPayload
   * @returns the updated customView including existing and updated properties (full
   * properties object is returned)
   */
  public updateCustomView = (customViewPayload: ICustomViewPayload): Observable<IUpdateCustomViewResponse> => {
    const url = `${this.api}/user-preferences?action=U`;
    const body: string = JSON.stringify(customViewPayload);
    const response: any = this.httpClient.post(url, body);
    return response;
  };

  /**
   * use back-end user preferences CRUD API to disable customView.
   * @param id
   * @returns
   */
  public deleteCustomView = (id: string): Observable<IDeleteCustomViewResponse> => {
    const url = `${this.api}/user-preferences?action=D`;
    const requestPayload: IDeleteCustomViewRequestPayload = {
      user_id: id,
    };
    const body = JSON.stringify(requestPayload);
    const response: any = this.httpClient.post(url, body);
    return response;
  };

  /**
   * build a new customView with id, name, and default initialState
   * @param id
   * @param name
   * @returns new customView
   */
  // public buildDefaultCustomView = (): ICustomView => {
  //   const customViewInitialState = this.buildDefaultState();
  //   return this.buildCustomView(customViewInitialState);
  // };

  public buildNewCustomView = () => {
    const customView = {
      status: CustomViewStatus.New,
    };
    return customView;
  };

  public buildInvalidCustomView = () => {
    const customView = {
      status: CustomViewStatus.Invalid,
    };
    return customView;
  };

  /**
   * build a new customView with id, name, and initialState
   * @param id
   * @param name
   * @param initialState
   * @returns new customView
   */
  public buildCustomView = (initialState?: ICustomViewInitialState): ICustomView => {
    const idTokenClaims = this.authService.session.account.idTokenClaims;
    const customView: ICustomView = {
      // note: cheatsheet to use the User GUID for key (instead of Email)
      // id: idTokenClaims?.oid,
      // Use Email for key
      user_id: idTokenClaims!.preferred_username,
      user_name: idTokenClaims!.name,
      custom_views: [],
      status: CustomViewStatus.Active,
    };
    //     else {
    //         // Add a default custom view if no initial state is provided
    //         customView.custom_views.push({
    //           customViewName: '',
    //           is_default: true,
    //           lastSaved: new Date().toLocaleString(),
    //           // Add other necessary properties for a custom view
    //         });
    //       }

    return customView;
  };

  public buildCustomViewFromPayload(payload: ICustomViewPayload): ICustomView {
    let customView: ICustomView = {
      status: payload.status,
      user_id: payload.user_id,
      user_name: payload.user_name,
    };
    const payloadState = payload.custom_views;
    if (payloadState) {
      customView.custom_views = payloadState.map(state => ({
        customViewName: state.customViewName,
        is_default: state.is_default,
        assetLayers: state.assetLayers,
        basemapName: state.basemapName,
        colorScheme: state.colorScheme,
        jurisdictions: state.jurisdictions,
        extensionLayer: state.extensionLayer,
        lastSaved: state.lastSaved,
        viewStateCenter: {
          lat: parseFloat(state.viewStateCenter.lat),
          lng: parseFloat(state.viewStateCenter.lng),
        },
        viewStateZoom: parseFloat(state.viewStateZoom),
      }));
    }
    return customView;
  }

  public buildPayloadFromCustomView(customView: ICustomView): ICustomViewPayload {
    let payload: ICustomViewPayload = {
      status: customView.status,
      user_id: customView.user_id,
      user_name: customView.user_name,
    };
    const customViewState = customView.custom_views;
    if (customViewState) {
      payload.custom_views = customViewState.map(state => ({
        customViewName: state.customViewName,
        is_default: state.is_default,
        assetLayers: state.assetLayers,
        basemapName: state.basemapName,
        colorScheme: state.colorScheme,
        lastSaved: state.lastSaved,
        jurisdictions: state.jurisdictions,
        extensionLayer: state.extensionLayer,
        viewStateCenter: {
          lat: state.viewStateCenter.lat.toString(),
          lng: state.viewStateCenter.lng.toString(),
        },
        viewStateZoom: state.viewStateZoom.toString(),
      }));
    }
    return payload;
  }

  public buildCurrentState = (): ICustomViewInitialState => {
    const currentState: ICustomViewInitialState = {
      customViewName: this.customViewName,
      is_default: this.isDefaultCustomView,
      assetLayers: filterAssetTypesByLayerDisplayList(this.layerVisibilityService.layerControlAssets(), true),
      basemapName: this.basemapName,
      colorScheme: this.colorScheme,
      jurisdictions: this.jurisdictionCodes,
      extensionLayer: this.extensionService.activeLayerDisplayName() || Extensions.None,
      viewStateCenter: this.viewStateCenter,
      viewStateZoom: this.viewStateZoom,
    };

    return currentState;
  };

  private getDefaultMapStyleConfigName = (mapStyleConfig: IBasemapsConfig | IColorSchemesConfig): string => {
    const mapStyleDefault = Object.values(mapStyleConfig).find((item: any) => {
      return item.default === true;
    });
    const defaultStyle = mapStyleConfig == basemaps ? "Dark" : "Circuit View";
    return mapStyleDefault ? mapStyleDefault.name : defaultStyle;
  };

  private setBaseMapStyleToUserPreference(map: any, defaultView: ICustomViewInitialState, holdMap: boolean) {
    for (let basemap in basemaps) {
      if (map && !holdMap && defaultView && basemaps[basemap].name == defaultView.basemapName) {
        map.setStyle(basemap);
        this.mapViewService.setViewStateStyle(basemap);
      }
    }

    for (const [key, value] of Object.entries(colorSchemes)) {
      if (map && !holdMap && defaultView && key == defaultView.colorScheme) {
        this.colorScheme = defaultView.colorScheme;
        this.mapViewService.setViewStateColorScheme(this.colorScheme as CircuitColorStyleScheme);
      }
    }
  }

  private setAssetLayersToUserPreference(defaultView: ICustomViewInitialState, holdMap: boolean) {
    if (defaultView && !holdMap) {
      const assetLayersForRendering = buildAssetTypesForRendering(defaultView.assetLayers);
      this.layerVisibilityService.setLayerControlAssets(assetLayersForRendering);
    }
  }

  private setExtensionLayerToUserPreference(defaultView: ICustomViewInitialState, holdMap: boolean) {
    if (holdMap || !defaultView) {
      return;
    }

    if (defaultView.extensionLayer && defaultView.extensionLayer !== Extensions.None) {
      this.extensionService.setExtensionFromDisplayName(defaultView.extensionLayer);
    } else if (defaultView.extensionLayer === Extensions.None && this.extensionService.activeExtensionKey()) {
      this.extensionService.reset();
    }
  }

  private flyMapToUserPreferenceCenter(map: any, defaultView: ICustomViewInitialState, holdMap: boolean) {
    if (map && !holdMap && defaultView) {
      map.flyTo({
        center: defaultView.viewStateCenter,
        zoom: defaultView.viewStateZoom,
      });
    }
  }

  private setJurisdictionSettings(defaultView: ICustomViewInitialState, holdMap: boolean) {
    if (defaultView && defaultView.jurisdictions && !holdMap) {
      this.store.dispatch(
        MapActions.setJurisdictions({
          payload: defaultView.jurisdictions,
        })
      );
    }
  }

  private findDefaultView(customView: ICustomView): ICustomViewInitialState {
    let customViews = customView.custom_views;
    const defaultView = customViews?.find(customView => customView.is_default == true);
    return defaultView || systemPresetViewConfig;
  }

  public setViewToDefaultPreferences(map: any, customView: ICustomView) {
    let defaultView: ICustomViewInitialState = this.findDefaultView(customView);
    const holdMap: boolean = customView.holdMap as boolean;
    if (defaultView) {
      this.setBaseMapStyleToUserPreference(map, defaultView as ICustomViewInitialState, holdMap);
      this.setAssetLayersToUserPreference(defaultView as ICustomViewInitialState, holdMap);
      this.setExtensionLayerToUserPreference(defaultView as ICustomViewInitialState, holdMap);
      this.flyMapToUserPreferenceCenter(map, defaultView as ICustomViewInitialState, holdMap);
      this.setJurisdictionSettings(defaultView as ICustomViewInitialState, holdMap);
    } else {
    }
  }

  public applyCustomView(map: any, view: ICustomViewInitialState) {
    this.setBaseMapStyleToUserPreference(map, view as ICustomViewInitialState, false);
    this.setAssetLayersToUserPreference(view as ICustomViewInitialState, false);
    this.setExtensionLayerToUserPreference(view as ICustomViewInitialState, false);
    this.flyMapToUserPreferenceCenter(map, view as ICustomViewInitialState, false);
    this.setJurisdictionSettings(view as ICustomViewInitialState, false);
    this.store.dispatch(CustomViewActions.applyCustomView({ payload: undefined }));
  }
}
